import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import {
  CreateScreeningService,
  CreatorSourceMethod,
} from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ScreeningListResponseDto } from '@/app/common/general-types/social-screening.dto';
import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { form, required, FormField } from '@angular/forms/signals';
import { DatePipe } from '@angular/common';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { Subscription } from 'rxjs';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { ImageService } from '@/app/services/image/image.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { BriefService } from '@/app/services/brief/brief.service';
import { FormatService } from '@/app/common/format/format.service';

/** A manually typed creator row, before it is turned into a screening list on submit. */
interface ManualCreatorRow {
  creatorName: string;
  instagram: string;
  tiktok: string;
}

interface CreatorMethodOption {
  id: CreatorSourceMethod;
  name: string;
  description: string;
}

/**
 * Wizard step 6 — who to screen. Four equal-weight methods, each revealing its own panel.
 *
 * "Select from SHOUT" is backed by the brand's saved screening lists (and, when the user arrived
 * from a brief assignment, the creators they already picked) rather than a separate directory.
 */
@Component({
  selector: 'sbs-creator-select-method',
  imports: [FormField, AddDocumentComponent, DatePipe, LdgImgComponent],
  templateUrl: './creator-select-method.html',
  styleUrl: './creator-select-method.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorSelectMethod implements OnInit, OnDestroy {

  readonly methods: CreatorMethodOption[] = [
    { id: 'one', name: 'Add one by one', description: 'Enter creator handles or names manually.' },
    { id: 'csv', name: 'Upload CSV file', description: 'Bulk-import a list of creators from a spreadsheet.' },
    { id: 'shout', name: 'Select from SHOUT', description: 'Pick from creators already in your network.' },
    { id: 'later', name: 'Add creators later', description: 'Submit the brief now and add creators any time.' },
  ];

  /** Services */
  public socialScreeningService = inject(SocialScreeningService);
  private currentUserService = inject(CurrentUserService);
  public createScreeningService = inject(CreateScreeningService);
  private uploadService = inject(S3uploaderService);
  public imageService = inject(ImageService);
  private briefService = inject(BriefService);
  private formatService = inject(FormatService);
  /** Services - END */

  readonly selectedMethod = computed(() => this.createScreeningService.creatorSourceMethod());

  readonly hasPreselectedCreators = computed(
    () => this.socialScreeningService.selectedCreators().length > 0,
  );

  readonly selectedCreatorCount = computed(() => this.socialScreeningService.selectedCreators().length);

  /** Drives the accent tally pill: how many creators this brief will screen. */
  readonly tally = computed<number>(() => {
    switch (this.selectedMethod()) {
      case 'one':
        return this.creators().filter((row) => this.isUsableRow(row)).length;
      case 'shout':
        return this.createScreeningService.selectedList()?.creators?.length ?? this.selectedCreatorCount();
      default:
        return 0;
    }
  });

  /** Crator List Values */
  creatorListModel = signal({
    listName: '',
    listDescription: '',
    googleDocUrl: '',
  })
  creatorListForm = form(this.creatorListModel, (schemaPath) => {
    required(schemaPath.listName, { message: 'List name is required' });
    required(schemaPath.listDescription, { message: 'List description is required' });
  })

  allLists = signal<ScreeningListResponseDto[]>([]);
  /** Crator List Values - END */

  /** Creators Table */
  creators = signal<ManualCreatorRow[]>([]);
  /** Creators Table - END */

  private readonly subscriptionList = new Subscription();

  ngOnInit(): void {
    this.getSocialScreeningLists();
    if (!this.selectedMethod() && this.createScreeningService.usePreselectedCreators()) {
      this.createScreeningService.creatorSourceMethod.set('shout');
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getSocialScreeningLists() {
    this.subscriptionList.add(
      this.socialScreeningService.getBusinessAccountSocialScreeningLists(this.currentUserService.focussedBrand.id).subscribe((response) => {
        this.allLists.set(response as ScreeningListResponseDto[]);
      }),
    );
  }

  /** Switching method clears whatever the other methods had staged, so submit reads one source. */
  selectMethod(method: CreatorSourceMethod): void {
    this.createScreeningService.creatorSourceMethod.set(method);
    this.createScreeningService.usePreselectedCreators.set(
      method === 'shout' && this.hasPreselectedCreators(),
    );
    if (method !== 'shout') {
      this.createScreeningService.selectedList.set(undefined);
    }
    if (method !== 'csv') {
      this.createScreeningService.uploadedCsvUrl.set(undefined);
    }
    if (method !== 'one') {
      this.createScreeningService.manualCreators.set([]);
    } else if (this.creators().length === 0) {
      this.creators.set([{ creatorName: '', instagram: '', tiktok: '' }]);
    }
  }

  selectList(list: ScreeningListResponseDto) {
    this.createScreeningService.selectedList.set(list);
  }

  onDocumentUploaded(event: { file: string, fileKey: string, fullUrl: string }) {
    this.createScreeningService.uploadedCsvUrl.set(event.fullUrl);
    this.creatorListModel.update(model => ({ ...model, googleDocUrl: event.fullUrl }));
  }

  addCreatorRow() {
    this.creators.update(creators => [...creators, { creatorName: '', instagram: '', tiktok: '' }]);
    this.syncManualCreators();
  }

  removeCreatorRow(index: number) {
    this.creators.update(creators => creators.filter((_, i) => i !== index));
    this.syncManualCreators();
  }

  updateCreatorField(index: number, field: 'creatorName' | 'instagram' | 'tiktok', value: string) {
    this.creators.update(creators => {
      const updated = [...creators];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    this.syncManualCreators();
  }

  /** Mirrors the usable rows onto the draft service, which is what submit reads. */
  private syncManualCreators(): void {
    this.createScreeningService.manualCreators.set(
      this.creators()
        .filter((row) => this.isUsableRow(row))
        .map((row) => ({
          name: row.creatorName.trim() || `@${this.normaliseHandle(row.instagram) || this.normaliseHandle(row.tiktok)}`,
          instagramHandle: this.normaliseHandle(row.instagram) || undefined,
          tiktokHandle: this.normaliseHandle(row.tiktok) || undefined,
        })),
    );
  }

  /** A row is usable once it has at least one handle — the API rejects the rest. */
  private isUsableRow(row: ManualCreatorRow): boolean {
    return !!(this.normaliseHandle(row.instagram) || this.normaliseHandle(row.tiktok));
  }

  private normaliseHandle(value: string): string {
    return value.trim().replace(/^@/, '');
  }

  getTemplate($event: unknown) {
    this.uploadService.secureCreatorCsvDownload();
  }

  creatorDisplayName(creator: CreatorResponseDto): string {
    const firstName = this.usableName(creator.firstName);
    const lastName = this.usableName(creator.lastName);
    if (firstName && lastName) {
      return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }
    const instagram = this.instagramHandle(creator);
    if (instagram) {
      return `@${instagram}`;
    }
    const tiktok = this.tiktokHandle(creator);
    if (tiktok) {
      return `@${tiktok}`;
    }
    return 'Unknown creator';
  }

  instagramHandle(creator: CreatorResponseDto): string | null {
    return this.socialScreeningService.instagramHandle(creator) ?? null;
  }

  tiktokHandle(creator: CreatorResponseDto): string | null {
    return this.socialScreeningService.tiktokHandle(creator) ?? null;
  }

  canScreenCreator(creator: CreatorResponseDto): boolean {
    return this.socialScreeningService.canScreenCreator(creator);
  }

  instagramFollowersLabel(creator: CreatorResponseDto): string | null {
    return this.followersLabel(creator.instaFollowers);
  }

  tiktokFollowersLabel(creator: CreatorResponseDto): string | null {
    return this.followersLabel(creator.tikTokFollowers);
  }

  private usableName(value: string | undefined): string {
    const trimmed = value?.trim() ?? '';
    if (!trimmed || /^[.\-_]+$/.test(trimmed)) {
      return '';
    }
    return trimmed;
  }

  private followersLabel(count: number | undefined): string | null {
    if (!count || count <= 0) {
      return null;
    }
    return this.formatService.convertIntToString(count);
  }

  removeSelectedCreator(creator: CreatorResponseDto): void {
    this.socialScreeningService.removeCreator(creator);
    this.briefService.creatorsToScreen.set(
      this.briefService.creatorsToScreen().filter(
        (application) => application.creator?.blazeFirebaseUid !== creator.blazeFirebaseUid,
      ),
    );
    if (this.socialScreeningService.selectedCreators().length === 0) {
      this.createScreeningService.usePreselectedCreators.set(false);
    }
  }

}
